/**
 * Pure URL-allowlist gate for federated remote-entry scripts. Lives in its
 * own module so both `manifest.ts` and `index.ts` (the MF SSOT for script
 * loading) can import it without forming a cycle through index.ts's
 * back-references to preload-registry.
 *
 * SSOT for the allowlist contract - anyone validating a federation URL
 * (manifest fetch, parcel loader, dynamic remote install) MUST go through
 * this function so the rule is visible in one auditable place.
 */
import { isCdnPath } from '@abax/platform';

// Canonical tenant origins. Production traffic terminates at *.abax.cloud
// and *.track2find.com - anything else flowing into a <script src> from
// /federation-config or env-config.json is treated as tampered and rejected.
// `localhost` / `127.0.0.1` stay open for the dev shell on http in
// non-production builds AND - independent of build flavor - whenever the
// document itself is served from a loopback origin. The CWE-829 defense
// targets documents served from tenant origins: there, a compromised
// runtime/federation config must not redirect script loads to a local
// service the user happens to run. A document already served from loopback
// is a developer workstation running the shell locally (e.g. a local stack
// using the production-built shell image), where loopback remotes are the
// point of the setup and any tainted config would have to come from the
// developer's own stack to begin with.
//
// SECURITY-NOTE (subdomain-takeover surface): the suffix match accepts ANY
// subdomain of these zones. If a `*.abax.cloud` or `*.track2find.com` CNAME
// is ever abandoned with a dangling DNS pointer at a third-party host
// (e.g. retired heroku/azure/cloudfront app), an attacker who claims that
// host can ship JS into shell origin via federation-config tampering. The
// hardening path is to replace the suffix wildcards with an explicit
// allowlist of canonical CDN/origin hostnames once production topology is
// enumerated.
export const ALLOWED_HOST_SUFFIXES = ['.abax.cloud', '.track2find.com'];
const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1']);

/**
 * `process.env.NODE_ENV` is webpack-injected at build time. In a production
 * bundle the dead-code elimination pass folds this constant to `false`,
 * leaving `isLoopbackDocument()` as the only loopback gate at runtime.
 */
const ALLOW_LOOPBACK = process.env.NODE_ENV !== 'production';

/**
 * Runtime check, deliberately NOT build-time: the same production-built
 * shell artifact must reject loopback remotes when served from a tenant
 * origin and accept them when served from a developer's local stack.
 * Evaluated per call so the gate follows the document, not the bundle.
 */
function isLoopbackDocument(): boolean {
  return LOCALHOST_HOSTNAMES.has(window.location.hostname);
}

/**
 * Validate that a remote URL targets a canonical tenant origin (https) or
 * the local dev shell (http://localhost) - the latter only in
 * non-production builds or when the document itself is on a loopback
 * origin. Defense-in-depth against CWE-829: a tainted /federation-config
 * or env-config.json cannot inject arbitrary JS into the shell origin via
 * a remote-entry redirect.
 */
export function validateRemoteUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (
      (ALLOW_LOOPBACK || isLoopbackDocument()) &&
      parsed.protocol === 'http:' &&
      LOCALHOST_HOSTNAMES.has(parsed.hostname)
    ) {
      return true;
    }
    // Same-origin, but ONLY under a CDN path this realm serves.
    //
    // An earlier revision accepted ANY same-origin URL and justified it as
    // narrower than the suffix rule below. That was wrong, and two reviews
    // caught it: it is narrower only for hosts already inside those zones. For
    // a host outside them it is new trust, and it would have let a tampered
    // federation config execute anything the shell's own origin happens to
    // serve, uploads included, through the classic loader where integrity is
    // optional.
    //
    // Restricting to the CDN paths keeps what re-homing actually needs — those
    // are exactly the URLs re-homing produces — and grants nothing else. Encoded
    // separators are refused inside `isCdnPath`, so a prefix cannot be walked
    // out of by a proxy that decodes before it routes.
    //
    // Known limit: a backend-supplied RELATIVE entry resolves to the document
    // origin outside any CDN path, so on a host beyond the canonical zones it is
    // still refused. That is unchanged from before this rule existed, and the
    // registered entries are absolute in every deployed environment.
    //
    // No protocol check is needed here: an origin match implies the document's
    // own scheme, so this can never downgrade an https document to http.
    if (
      typeof window !== 'undefined' &&
      parsed.origin === window.location.origin &&
      isCdnPath(parsed.pathname)
    ) {
      return true;
    }
    if (parsed.protocol !== 'https:') {
      return false;
    }
    return ALLOWED_HOST_SUFFIXES.some(
      (suffix) => parsed.hostname === suffix.slice(1) || parsed.hostname.endsWith(suffix),
    );
  } catch {
    return false;
  }
}
